import axios from "axios";

import { setToken, getToken, removeToken, tokenFromError, isUnauthorized } from "@/utils/auth";
import { User } from "@/lib/types";
import { getRuntimeConfig } from "@/lib/runtime-config";

// 401 handling for authenticated requests (public auth endpoints, see
// PUBLIC_PATHS, never send a token, so they are skipped here).
//
// Multi-tab token rotation: another tab can rotate or clear the token while this
// tab has requests in flight, so a 401 may only mean "I used an old token".
//
// 1. Settle first. Tab A stores a rotated token right after its response lands
//    (see lib/account.ts), which can be a few ms after tab B's old-token request
//    already got its 401. So we wait AUTH_SETTLE_MS and re-read storage before
//    deciding anything; a removal is only ever based on the settled value.
// 2. Stale token (storage now holds a different one): never remove it. Replay
//    only requests that are safe and can only reach our API with a `Token `
//    credential (GET/HEAD/OPTIONS), once, under the current token. Anything else
//    (POST/PATCH/...) is NOT replayed, so a write is never silently re-issued as
//    a different user; the error is surfaced and the page reloads to adopt the
//    new session.
// 3. Same token still stored after settling: it is really invalid, so remove it
//    (other tabs see that via the storage event and re-check before logging out).
// 4. Nothing stored (another tab logged out): go to login.
const AUTH_SETTLE_MS = 300;
const SAFE_METHODS = ["get", "head", "options"];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function targetsApiHost(config: { url?: string; baseURL?: string }): Promise<boolean> {
  try {
    const { apiHost } = await getRuntimeConfig();
    const full = new URL(config.url ?? "", config.baseURL ?? window.location.href);
    return full.origin === new URL(apiHost, window.location.href).origin;
  } catch {
    return false;
  }
}

async function canReplay(config: {
  method?: string;
  url?: string;
  baseURL?: string;
  headers?: { Authorization?: unknown };
  _authRetried?: boolean;
}): Promise<boolean> {
  const auth = config.headers?.Authorization;
  return (
    !config._authRetried &&
    SAFE_METHODS.includes((config.method ?? "get").toLowerCase()) &&
    typeof auth === "string" &&
    auth.startsWith("Token ") &&
    (await targetsApiHost(config))
  );
}

axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (
      isUnauthorized(err) &&
      err?.config?.headers?.Authorization &&
      typeof window !== "undefined"
    ) {
      const used = tokenFromError(err);
      await sleep(AUTH_SETTLE_MS);
      const current = getToken();

      if (current && used && used !== current) {
        // Stale token: someone else rotated it. Leave storage alone.
        if (await canReplay(err.config)) {
          err.config._authRetried = true;
          err.config.headers.Authorization = `Token ${current}`;
          return axios(err.config);
        }
        // Not safe to replay: surface the error and pick up the new session.
        window.location.reload();
        return Promise.reject(err);
      }
      if (current && used === current) removeToken();
      if (!getToken()) window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// Unauthenticated auth endpoints. A stale token in storage must not be sent to
// these: the server could 401 it, which would trigger the interceptor's token
// removal + reload to /login and lose the real error (e.g. "Invalid credentials").
const PUBLIC_PATHS = [
  "/login/",
  "/register/",
  "/verify-email/",
  "/resend-verification/",
  "/forgot-password/",
  "/reset-password/",
];

const isPublicPath = (path: string) => PUBLIC_PATHS.includes(path.split("?")[0]);

export const apiRequest = async <T>(
  method: "get" | "post" | "put" | "patch" | "delete",
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any = null
): Promise<T> => {
  const token = isPublicPath(path) ? null : getToken();
  const { apiHost } = await getRuntimeConfig();
  const config = {
    method,
    url: `${apiHost}${path}`,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Token ${token}` }),
    },
    ...(data && { data }),
  };

  const response = await axios(config);
  return response.data;
};

export const apiRequestBlob = async (
  method: "get" | "post",
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any = null
): Promise<{ blob: Blob; headers: Record<string, string> }> => {
  const token = getToken();
  const { apiHost } = await getRuntimeConfig();
  const response = await axios({
    method,
    url: `${apiHost}${path}`,
    responseType: "blob",
    headers: {
      ...(data && { "Content-Type": "application/json" }),
      ...(token && { Authorization: `Token ${token}` }),
    },
    ...(data && { data }),
  });
  return { blob: response.data as Blob, headers: response.headers as Record<string, string> };
};

export const login = async (
  email: string,
  password: string
): Promise<string> => {
  const data = await apiRequest<{ token: string }>("post", "/login/", {
    email,
    password,
  });
  setToken(data.token);
  return data.token;
};

export const register = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string
): Promise<void> => {
  // No token is returned: the account must verify its email before signing in.
  await apiRequest("post", "/register/", {
    first_name: firstName,
    last_name: lastName,
    email,
    password,
  });
};

export const verifyEmail = async (token: string, password: string): Promise<void> => {
  await apiRequest("post", "/verify-email/", { token, password });
};

export const resendVerification = async (email: string): Promise<void> => {
  await apiRequest("post", "/resend-verification/", { email });
};

export const getProfile = async (): Promise<User> => {
  return await apiRequest<User>("get", "/me/");
};

export const logout = async (): Promise<void> => {
  try {
    await apiRequest("post", "/logout/");
  } catch (err) {
    console.error("Logout failed:", err);
  }

  removeToken();
};

export const resetPassword = async (
  token: string,
  password: string
): Promise<void> => {
  await apiRequest("post", "/reset-password/", { token, password });
};
